import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CodeEditor from '@uiw/react-textarea-code-editor';
import GlowCard from '../../../shared/GlowCard';
import { useColors, useColorMode } from '../../../theme/ColorModeContext';

export const sectionTitleSx = {
    fontFamily: '"Orbitron", sans-serif',
    fontWeight: 700,
    letterSpacing: '0.01em',
    mb: 1.5,
};

export const bodySx = {
    lineHeight: 1.75,
    fontSize: '1rem',
};

interface StageSectionProps {
    title?: string;
    eyebrow?: string;
    gradientBorder?: boolean;
    accentTop?: boolean;
    children: React.ReactNode;
}

export const StageSection = ({ title, eyebrow, gradientBorder, accentTop, children }: StageSectionProps) => {
    const colors = useColors();
    return (
        <GlowCard glowOnHover={false} gradientBorder={gradientBorder} accentTop={accentTop} sx={{ mb: '24px' }}>
            <Box sx={{ p: { xs: '22px', md: '30px' } }}>
                {eyebrow && (
                    <Typography sx={{
                        color: colors.accent.cyan,
                        fontFamily: '"Orbitron", sans-serif',
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        mb: 1,
                    }}>
                        {eyebrow}
                    </Typography>
                )}
                {title && (
                    <Typography variant="h4" sx={{ ...sectionTitleSx, color: colors.text.primary }}>
                        {title}
                    </Typography>
                )}
                {children}
            </Box>
        </GlowCard>
    );
};

export const Prose = ({ children }: { children: React.ReactNode }) => {
    const colors = useColors();
    return <Typography sx={{ ...bodySx, color: colors.text.secondary, mb: 2, '&:last-child': { mb: 0 } }}>{children}</Typography>;
};

export const KeyPoints = ({ items }: { items: React.ReactNode[] }) => {
    const colors = useColors();
    return (
        <Box component="ul" sx={{ color: colors.text.secondary, lineHeight: 1.75, m: 0, pl: '20px' }}>
            {items.map((item, idx) => (
                <Box component="li" key={idx} sx={{ mb: 0.75, '&:last-child': { mb: 0 } }}>{item}</Box>
            ))}
        </Box>
    );
};

export const FactGrid = ({ items, columns = 2 }: { items: [string, string][]; columns?: number }) => {
    const colors = useColors();
    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: `repeat(${columns}, 1fr)` },
            gap: '14px',
        }}>
            {items.map(([label, detail]) => (
                <Box key={label} sx={{
                    p: '16px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border.subtle}`,
                    backgroundColor: colors.bg.tertiary,
                }}>
                    <Typography sx={{ color: colors.text.primary, fontWeight: 800, mb: 0.75 }}>{label}</Typography>
                    <Typography sx={{ color: colors.text.secondary, lineHeight: 1.6, fontSize: '0.92rem' }}>{detail}</Typography>
                </Box>
            ))}
        </Box>
    );
};

export const NumberedFlow = ({ steps }: { steps: [string, string][] }) => {
    const colors = useColors();
    return (
        <Box sx={{ display: 'grid', gap: '14px' }}>
            {steps.map(([title, body], index) => (
                <Box key={title} sx={{ display: 'grid', gridTemplateColumns: '42px 1fr', gap: '14px' }}>
                    <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        color: colors.bg.primary,
                        background: colors.gradient.primary,
                        fontWeight: 800,
                        fontFamily: '"Orbitron", sans-serif',
                    }}>
                        {index + 1}
                    </Box>
                    <Box>
                        <Typography sx={{ color: colors.text.primary, fontWeight: 700, mb: 0.5 }}>{title}</Typography>
                        <Typography sx={{ ...bodySx, color: colors.text.secondary, fontSize: '0.94rem' }}>{body}</Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export const StageDivider = () => {
    const colors = useColors();
    return <Divider sx={{ borderColor: colors.border.subtle, my: 2.5 }} />;
};

export const MiniLabel = ({ children }: { children: React.ReactNode }) => {
    const colors = useColors();
    return (
        <Typography sx={{ color: colors.text.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
            {children}
        </Typography>
    );
};

/** An inset box for widget controls and results; `borderColor` flags a state (ok, warning, ...). */
export const Panel = ({ children, borderColor }: { children: React.ReactNode; borderColor?: string }) => {
    const colors = useColors();
    return (
        <Box sx={{
            p: '18px',
            borderRadius: '8px',
            backgroundColor: colors.bg.tertiary,
            border: `1px solid ${borderColor ?? colors.border.subtle}`,
            transition: 'border-color 0.25s ease',
            minWidth: 0,
        }}>
            {children}
        </Box>
    );
};

type CalloutTone = 'info' | 'security' | 'warning';

export const Callout = ({ tone = 'info', title, children }: { tone?: CalloutTone; title: string; children: React.ReactNode }) => {
    const colors = useColors();
    const accent = { info: colors.accent.purple, security: colors.accent.cyan, warning: colors.accent.orange }[tone];
    return (
        <Box sx={{
            my: 2.5,
            p: '16px 18px',
            borderRadius: '8px',
            borderLeft: `3px solid ${accent}`,
            backgroundColor: colors.bg.tertiary,
        }}>
            <Typography sx={{ color: accent, fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.75 }}>
                {title}
            </Typography>
            <Typography component="div" sx={{ color: colors.text.secondary, lineHeight: 1.7, fontSize: '0.95rem' }}>
                {children}
            </Typography>
        </Box>
    );
};

/** Read-only, syntax-highlighted snippet, styled like the challenge page's markdown code blocks. */
export const CodeSample = ({ code, language, caption }: { code: string; language: string; caption?: string }) => {
    const colors = useColors();
    const { mode } = useColorMode();
    return (
        <Box sx={{ my: 2.5, minWidth: 0 }}>
            {caption && <MiniLabel>{caption}</MiniLabel>}
            <Box sx={{ overflowX: 'auto', borderRadius: '6px', border: `1px solid ${colors.border.default}`, backgroundColor: colors.bg.tertiary }}>
                <CodeEditor
                    value={code.trim()}
                    language={language}
                    data-color-mode={mode}
                    disabled={true}
                    padding={16}
                    style={{
                        fontSize: 14,
                        fontFamily: '"Share Tech Mono", ui-monospace, SFMono-Regular, SF Mono, Consolas, Liberation Mono, Menlo, monospace',
                        backgroundColor: 'transparent',
                        minWidth: 'max-content',
                    }}
                />
            </Box>
        </Box>
    );
};

interface SegmentedToggleProps<T extends string> {
    value: T;
    options: { value: T; label: React.ReactNode }[];
    onChange: (value: T) => void;
    ariaLabel: string;
}

export const SegmentedToggle = <T extends string>({ value, options, onChange, ariaLabel }: SegmentedToggleProps<T>) => {
    const colors = useColors();
    return (
        <ToggleButtonGroup
            value={value}
            exclusive
            onChange={(_, next) => { if (next) onChange(next as T); }}
            size="small"
            aria-label={ariaLabel}
            sx={{
                flexWrap: 'wrap',
                '& .MuiToggleButton-root': {
                    color: colors.text.secondary,
                    borderColor: colors.border.default,
                    fontFamily: '"Orbitron", sans-serif',
                    fontSize: '0.72rem',
                    letterSpacing: '0.04em',
                    px: 1.5,
                },
                '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
                    color: colors.accent.cyan,
                    backgroundColor: colors.accent.cyanGlow,
                },
            }}
        >
            {options.map((option) => (
                <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
};
